import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { validateSignalREndpoint } from '../realtime/endpoint';

const SignalRContext = createContext(null);
const DEFAULT_ENDPOINT = process.env.REACT_APP_SIGNALR_API_URL || process.env.REACT_APP_API_URL;

// One connection per signed-in browser tab. Notifications are never Redux actions.
export function SignalRProvider({ children, enabled, sessionKey, endpoint = DEFAULT_ENDPOINT }) {
  // V2 owns its authenticated connection. Never also open the legacy hub.
  enabled = enabled && process.env.REACT_APP_REALTIME_V2_ENABLED !== 'true';
  const [status, setStatus] = useState('Disconnected');
  const [clientId] = useState(() => crypto.randomUUID());
  const connectionRef = useRef(null);
  const listeners = useRef(new Set());
  const domainListeners = useRef(new Set());
  const pending = useRef(new Map());
  const subscribe = useCallback((listener) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);
  const subscribeDomain = useCallback(listener => {
    domainListeners.current.add(listener);
    return () => domainListeners.current.delete(listener);
  }, []);

  useEffect(() => {
    if (!enabled || !endpoint) return undefined;
    let disposed = false;
    let retryTimer;
    const requests = pending.current;
    let connection;
    try {
      connection = new HubConnectionBuilder()
      .withUrl(validateSignalREndpoint(endpoint), { withCredentials: true, timeout: 15000 })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.None).build();
    } catch {
      setStatus('Configuration error');
      return undefined;
    }
    connection.serverTimeoutInMilliseconds = 30000;
    connection.keepAliveIntervalInMilliseconds = 15000;
    connectionRef.current = connection;
    connection.on('DomainChanged', event => {
      if (disposed) return;
      domainListeners.current.forEach(listener => {
        try { listener(event); } catch { /* Isolate refresh owners. */ }
      });
    });
    connection.on('ReceiveAction', (action) => {
      if (disposed) return;
      const request = requests.get(action?.payload?.id);
      if (action?.payload?.clientId === clientId && request
        && action.type === request.type && action.payload.scope === request.scope) request.finish('confirmed');
      listeners.current.forEach((listener) => {
        try { listener(action); } catch { /* A subscriber cannot interrupt other pages. */ }
      });
    });
    const start = async () => {
      if (disposed) return;
      setStatus('Connecting');
      try {
        await connection.start();
        if (disposed) { await connection.stop(); return; }
        setStatus('Connected');
      } catch {
        if (!disposed) {
          setStatus('Disconnected — retrying');
          clearTimeout(retryTimer);
          retryTimer = setTimeout(start, 10000);
        }
      }
    };
    connection.onreconnecting(() => {
      if (!disposed) {
        requests.forEach(request => request.finish('disconnected'));
        setStatus('Reconnecting');
      }
    });
    connection.onreconnected(() => { if (!disposed) setStatus('Connected'); });
    connection.onclose(() => {
      if (!disposed) {
        requests.forEach(request => request.finish('disconnected'));
        setStatus('Disconnected — retrying');
        clearTimeout(retryTimer);
        retryTimer = setTimeout(start, 10000);
      }
    });
    // StrictMode's setup/cleanup probe must not initiate a throwaway negotiation.
    // start checks disposed before touching the transport.
    Promise.resolve().then(start);
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      connectionRef.current = null;
      requests.forEach((request) => request.finish('disconnected'));
      connection.stop().catch(() => {});
    };
  }, [enabled, endpoint, sessionKey, clientId]);

  // Resolve delivery outcomes without making an already persisted API save fail.
  const publish = useCallback((action) => {
    const connection = connectionRef.current;
    if (!enabled || connection?.state !== 'Connected') return Promise.resolve('disconnected');
    if (!action || typeof action.type !== 'string' || !action.type) return Promise.resolve('not sent');
    const id = crypto.randomUUID();
    return new Promise((resolve) => {
      let timer;
      const finish = (result) => {
        clearTimeout(timer);
        pending.current.delete(id);
        resolve(result);
      };
      timer = setTimeout(() => finish('not confirmed'), 15000);
      pending.current.set(id, { finish, type: action.type, scope: action.payload?.scope });
      Promise.resolve().then(() => connection.send('SendAction', { ...action, payload: { ...action.payload, clientId, id } }))
        .catch(() => finish('not sent'));
    });
  }, [clientId, enabled]);
  const visibleStatus = !enabled ? 'Disconnected' : !endpoint ? 'Not configured' : status;
  const value = useMemo(() => ({ status: visibleStatus, clientId, subscribe, subscribeDomain, publish, endpoint }),
    [visibleStatus, clientId, subscribe, subscribeDomain, publish, endpoint]);
  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
}

export function useSignalR() {
  const context = useContext(SignalRContext);
  if (!context) throw new Error('useSignalR must be used within SignalRProvider');
  return context;
}
